<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-12 15:52:37
 * @LastEditors: LiZhiWei
 * @LastEditTime: 2026-08-18 16:08:50
 * @Description: 可展开画廊，PC 端 hover 激活，移动端滑动停止后阻尼吸附居中
-->
<template>
  <div :class="cn('flex w-full gap-4', props.class)" @mouseleave="reset">
    <template v-if="items">
      <div
        v-for="(item, index) in items"
        :key="index"
        :ref="(el) => setItemRef(el as HTMLElement, index)"
        class="relative flex min-w-0 cursor-pointer overflow-hidden transition-all duration-500 ease-in-out"
        :style="{
          flexGrow: activeIndex === index ? 'var(--eg-active, 3)' : 'var(--eg-inactive, 1)',
          flexShrink: 'var(--eg-item-shrink, 1)',
          flexBasis:
            activeIndex === index ? 'var(--eg-active-basis, 0%)' : 'var(--eg-inactive-basis, 0%)',
        }"
        @mouseenter="setActive(index)"
        @click="onCardClick(index)"
      >
        <slot :item="item" :index="index" :active="activeIndex === index"></slot>
      </div>
    </template>

    <template v-else>
      <div
        v-for="(image, index) in list"
        :key="image as string"
        :ref="(el) => setItemRef(el as HTMLElement, index)"
        class="relative flex min-w-0 cursor-pointer overflow-hidden transition-all duration-500 ease-in-out"
        :style="{
          flexGrow: activeIndex === index ? 'var(--eg-active, 3)' : 'var(--eg-inactive, 1)',
          flexShrink: 'var(--eg-item-shrink, 1)',
          flexBasis:
            activeIndex === index ? 'var(--eg-active-basis, 0%)' : 'var(--eg-inactive-basis, 0%)',
        }"
        @mouseenter="setActive(index)"
        @click="onCardClick(index)"
      >
        <img
          class="relative h-full w-full object-cover"
          :src="image as string"
          :alt="image as string"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup generic="T">
  import type { HTMLAttributes } from 'vue'

  import { cn } from '~/utils/cn'

  interface Props {
    class?: HTMLAttributes['class']
    defaultIndex?: number
    images?: string[]
    items?: T[]
  }

  const props = withDefaults(defineProps<Props>(), {
    class: undefined,
    defaultIndex: 0,
    images: undefined,
    items: undefined,
  })

  defineSlots<{
    default(props: { active: boolean; index: number; item: T }): unknown
  }>()

  const activeIndex = ref(props.defaultIndex)
  const itemRefs = ref<HTMLElement[]>([])
  const rootRef = ref<HTMLElement | null>(null)

  let scrollTimer: ReturnType<typeof setTimeout> | undefined
  let isProgrammaticScroll = false

  function setActive(index: number) {
    activeIndex.value = index
  }

  function reset() {
    activeIndex.value = props.defaultIndex
  }

  /** 收集子元素引用，同时找到滚动容器 */
  function setItemRef(el: HTMLElement | null, index: number) {
    if (el) {
      itemRefs.value[index] = el
      if (!rootRef.value) rootRef.value = el.parentElement
    }
  }

  /** 判断容器是否为横向滚动模式 */
  function isScrollContainer(): boolean {
    const container = rootRef.value
    if (!container) return false
    const computed = window.getComputedStyle(container)
    return computed.overflowX === 'auto' || computed.overflowX === 'scroll'
  }

  /** 找到当前滚动位置最接近容器中心的卡片索引 */
  function getCenterIndex(): number {
    const container = rootRef.value
    if (!container) return 0

    const centerX = container.scrollLeft + container.clientWidth / 2
    let minDist = Infinity
    let result = 0

    itemRefs.value.forEach((el, i) => {
      if (!el) return
      const elCenter = el.offsetLeft + el.offsetWidth / 2
      const dist = Math.abs(elCenter - centerX)
      if (dist < minDist) {
        minDist = dist
        result = i
      }
    })

    return result
  }

  /** 将指定卡片平滑滚动到容器中央 */
  function scrollToIndex(index: number) {
    const container = rootRef.value
    const target = itemRefs.value[index]
    if (!container || !target) return

    const targetLeft = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2

    isProgrammaticScroll = true

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'

    container.scrollTo({ left: Math.max(0, targetLeft), behavior })

    // 平滑滚动动画结束后解除锁
    if (scrollTimer) clearTimeout(scrollTimer)
    scrollTimer = setTimeout(() => {
      isProgrammaticScroll = false
    }, 500)
  }

  /** 点击卡片时激活并居中 */
  function onCardClick(index: number) {
    setActive(index)
    scrollToIndex(index)
  }

  /** 滚动停止后自动吸附到最近的卡片 */
  function onScroll() {
    if (isProgrammaticScroll) return

    if (scrollTimer) clearTimeout(scrollTimer)
    scrollTimer = setTimeout(() => {
      const idx = getCenterIndex()
      if (idx !== activeIndex.value) {
        activeIndex.value = idx
      }
      // CSS scroll-snap 负责物理吸附居中，JS 只需同步激活态
      // 仅在未启用 snap 的容器中才做 JS 二次吸附
      const container = rootRef.value
      if (container) {
        const snapType = window.getComputedStyle(container).scrollSnapType
        if (snapType === 'none') scrollToIndex(idx)
      }
    }, 60)
  }

  const list = computed(() => {
    if (props.items) return props.items
    if (props.images) return props.images
    return []
  })

  onMounted(() => {
    if (!import.meta.client) return

    const container = rootRef.value
    if (!container || !isScrollContainer()) return

    container.addEventListener('scroll', onScroll, { passive: true })
  })

  onBeforeUnmount(() => {
    const container = rootRef.value
    if (container) {
      container.removeEventListener('scroll', onScroll)
    }
    if (scrollTimer) clearTimeout(scrollTimer)
  })
</script>
